import { Box, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AddressBookRecord, UserRecord } from '@tassat/fred-definitions';
import { groupBy, orderBy } from 'lodash';
import React from 'react';

import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

import AddressBookAgGrid from './ABookAgGrid';
import { AddressBookGridOptions } from './ABookGridOptions';
import { SearchParameters } from '../searchPanel/types';

const useAddressBookGridContainerStyles = makeStyles((theme: ApplicationMuiTheme) => {
    return {
        root: {
            marginLeft: theme.spacing(3),
            marginRight: theme.spacing(3),
            position: 'absolute',
            overflow: 'auto',
            inset: '200px 0px 70px 120px',
            //info - reserve space for scrollbar so that we have a predefined space for scrollbar to render
            // and not push content to the left.
            //This is done to prevent grids from pushing to the left which triggers the horizontal scroll view. sizeColumnsToFit() does not
            //work to readjust the columns when rendering multiple grids on a single page. Refer to Github
            // ag-grid issue #3354 for more details.
            scrollbarGutter: 'stable'
        },
        cardGroup: {
            marginRight: theme.spacing(3),
            padding: `${theme.spacing(1.375)}px ${theme.spacing(2)}px ${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
            borderRadius: '5px',
            border: `1px solid ${theme?.app?.applicationColors.enumColors.greyLighter || '#cfcfcf'}`,
            borderLeft: `4px solid ${theme?.app?.clientTheme.colors.highlightColor3 || '#B9D9EC'}`,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch'
        },
        firmName: {
            fontWeight: 900,
            color: theme?.app?.clientTheme.colors.primaryColor,
            lineHeight: '1rem',
            paddingLeft: '10px'
        },
        walletCount: {
            color: theme?.app?.applicationColors.enumColors.greyDark,
            fontSize: '0.7rem',
            paddingLeft: '10px'
        },
        gridItem: {
            width: '100%',
            flex: 1
        }
    };
});

export type AddressBookGridContainerProps = {
    contacts: AddressBookRecord[];
    editContact: any;
    user: UserRecord;
    areRecordsExpanded: boolean;
    searchParams: SearchParameters;
};

const AddressBookGridContainer: React.FC<AddressBookGridContainerProps> = ({
    contacts,
    editContact,
    user,
    areRecordsExpanded,
    searchParams
}) => {
    const OFFSET_TO_HIDE_SCROLLBAR = 4;
    const classes = useAddressBookGridContainerStyles();
    const [ rowGroupState, setRowGroupState ] = React.useState<Record<string, boolean>>({});

    const groupedContacts = React.useMemo((): Record<string, Readonly<AddressBookRecord[]>> => {
        return groupBy(contacts, (contact: AddressBookRecord): string => contact.FirmName);
    }, [contacts]);

    //info - On data mutation, make row group remain in previous state of being either expanded or collapsed
    React.useEffect((): void => {
        const newrowGroupState = Object.keys(groupedContacts)
            .reduce((accumulator: Record<string, boolean>, value: string): Record<string, boolean> => {
                accumulator[value] = rowGroupState[value] ?? false;

                return accumulator;
            }, {});

        setRowGroupState(newrowGroupState);
    }, [contacts]);

    //info - Make expand/minimize all take precedence over single expansion or collapse
    React.useEffect((): void => {
        const newrowGroupState = Object.keys(groupedContacts)
            .reduce((accumulator: Record<string, boolean>, value: string): Record<string, boolean> => {
                accumulator[value] = areRecordsExpanded;

                return accumulator;
            }, {});

        setRowGroupState(newrowGroupState);
    }, [areRecordsExpanded]);

    const formattedcontacts = React.useMemo((): Record<string, Readonly<AddressBookRecord[]>> => {
        //filter contacts based on the search parameters - firmName, bankName or walletName
        const filteredContacts: AddressBookRecord[] = contacts.filter((contact: AddressBookRecord): boolean => {
            const { client } = searchParams;

            if (client.groupedBy === '') {
                return true;
            } else if (client.groupedBy === 'firmName') {
                if (contact.FirmName === client.value) return true;
            } else if (client.groupedBy === 'walletName') {
                const doesNickNameExists = contact.NickName;

                if (doesNickNameExists) {
                    const nickNames = `${contact.NickName}`;

                    if (nickNames === client.value) return true;
                } else {
                    if (`${contact.AccountName}` === client.value) return true;
                }
            }
        });

        //sort by firmName
        const sortedContacts: AddressBookRecord[] = orderBy(filteredContacts, [(contact: AddressBookRecord): string => contact.FirmName.toLowerCase()], ['asc']);

        //group wallets by firmName
        const groupedContacts: Record<string, Readonly<AddressBookRecord[]>> =
        groupBy(sortedContacts, (contact: AddressBookRecord): string => contact.FirmName);

        //within groups, sort based on walletName/nickName
        for (const key in groupedContacts) {
            const value = groupedContacts[key];

            groupedContacts[key] = orderBy(value, (contact: AddressBookRecord): string => {
                const nickName = contact.NickName.toLowerCase();
                const walletName1 = contact.AccountName.toLowerCase();

                return nickName ? nickName : `${walletName1}`;
            });
        }

        return groupedContacts;
    }, [ contacts, searchParams ]);

    const handleRowGroupClick = (rowId: string): void => {
        const newRowGroupState = { ...rowGroupState };
        newRowGroupState[rowId] = !newRowGroupState[rowId];

        setRowGroupState(newRowGroupState);
    };

    return (
        <div className={classes.root}>
            {
                Object.keys(formattedcontacts)
                    .map((key: string): JSX.Element => {
                        const isRowGroupOpen: boolean = rowGroupState[key];

                        return (
                            <div key={key}>
                                <Box mt={1} />
                                <Grid
                                    alignItems='center'
                                    className={classes.cardGroup}
                                    container
                                    style={{
                                        height: isRowGroupOpen ?
                                            formattedcontacts[key].length >= 5 ?
                                                `${5 * AddressBookGridOptions.rowHeight + OFFSET_TO_HIDE_SCROLLBAR + AG_GRID_SMALL_HEADER_HEIGHT}px`
                                                : `${formattedcontacts[key].length * AddressBookGridOptions.rowHeight + OFFSET_TO_HIDE_SCROLLBAR + AG_GRID_SMALL_HEADER_HEIGHT}px`
                                            : `${AddressBookGridOptions.rowHeight + OFFSET_TO_HIDE_SCROLLBAR + AG_GRID_SMALL_HEADER_HEIGHT}px`
                                    }}
                                >
                                    <Grid
                                        item
                                        lg={2}
                                        onClick={() => formattedcontacts[key].length > 1 && handleRowGroupClick(key)}
                                        style={{ cursor: formattedcontacts[key].length > 1 ? 'pointer' : 'default', alignSelf: 'center' }}
                                        xs={2}
                                    >
                                        <Typography
                                            className={classes.firmName}
                                            component='span'
                                            variant='body1'
                                        >
                                            {key}
                                            <Typography
                                                className={classes.walletCount}
                                                display='block'
                                                variant='body1'
                                            >
                                                {`${formattedcontacts[key].length} Account${formattedcontacts[key].length > 1 ? 's' : ''}`}
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        className={classes.gridItem}
                                        item
                                        lg={10}
                                        xs={10}
                                    >
                                        <AddressBookAgGrid
                                            areRecordsExpanded={areRecordsExpanded}
                                            contacts={{
                                                groupId: key,
                                                first: formattedcontacts[key].slice(0, 1),
                                                others: formattedcontacts[key].slice(1)
                                            }}
                                            editContact={editContact}
                                            rowGroupState={rowGroupState}
                                            user={user}
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                        );
                    })
            }
        </div>
    );
};

export default AddressBookGridContainer;